import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { Snackbar, Alert, AlertColor, Slide, SlideProps } from '@mui/material';
import { NotificationInfo } from '../utils/errorHandler';

interface NotificationContextType {
  showNotification: (notification: NotificationInfo) => void;
  showSuccess: (message: string, details?: string) => void;
  showError: (message: string, details?: string) => void;
  showWarning: (message: string, details?: string) => void;
  showInfo: (message: string, details?: string) => void;
  hideNotification: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

interface NotificationProviderProps {
  children: ReactNode;
}

// Slide transition for notifications
function SlideTransition(props: SlideProps) {
  return <Slide {...props} direction="up" />;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [notification, setNotification] = useState<NotificationInfo | null>(null);
  const [open, setOpen] = useState(false);

  const showNotification = useCallback((notification: NotificationInfo) => {
    setNotification(notification);
    setOpen(true);
  }, []);

  const showSuccess = useCallback((message: string, details?: string) => {
    showNotification({
      message,
      type: 'success',
      details
    });
  }, [showNotification]);

  const showError = useCallback((message: string, details?: string) => {
    showNotification({
      message,
      type: 'error',
      details
    });
  }, [showNotification]);

  const showWarning = useCallback((message: string, details?: string) => {
    showNotification({
      message,
      type: 'warning',
      details
    });
  }, [showNotification]);

  const showInfo = useCallback((message: string, details?: string) => {
    showNotification({
      message,
      type: 'info',
      details
    });
  }, [showNotification]);

  const hideNotification = useCallback(() => {
    setOpen(false);
    // Clear notification after animation
    setTimeout(() => setNotification(null), 300);
  }, []);

  const handleClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    hideNotification();
  };

  const contextValue: NotificationContextType = {
    showNotification,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    hideNotification
  };

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
      
      {notification && (
        <Snackbar
          open={open}
          autoHideDuration={(() => {
            // Best practice durations based on notification type
            switch (notification.type) {
              case 'error':
                return 20000; // 20 seconds for errors/alerts
              case 'warning':
                return 10000; // 10 seconds for warnings
              case 'success':
                return 5000;  // 5 seconds for success
              case 'info':
                return 7000;  // 7 seconds for info
              default:
                return 6000;  // 6 seconds default
            }
          })()}
          onClose={handleClose}
          TransitionComponent={SlideTransition}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          sx={{ mb: 2, mr: 2 }}
        >
          <Alert
            onClose={handleClose}
            severity={notification.type as AlertColor}
            variant="filled"
            sx={{ width: '100%', minWidth: 300 }}
          >
            <div>
              <div>{notification.message}</div>
              {notification.details && (
                <div style={{ fontSize: '0.875rem', opacity: 0.8, marginTop: 4 }}>
                  {notification.details}
                </div>
              )}
            </div>
          </Alert>
        </Snackbar>
      )}
    </NotificationContext.Provider>
  );
};

export const useNotifications = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export default NotificationProvider;
